import { useContext, useEffect, useCallback, useRef, useState, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { DefaultButton, Spinner, SpinnerSize } from '@fluentui/react'

import { getCandidateList, getCandidateDetail, disableCandidate, enableCandidate, CandidateSummary, CandidateDetail } from '../../api'
import { AppStateContext } from '../../state/AppProvider'

import styles from './Candidates.module.css'

const PAGE_SIZE_OPTIONS = [200, 500]

// Renders a labelled section with key/value rows, skipping empty values.
const DetailSection = ({ title, children }: { title: string; children: [string, any][] }) => {
  const rows = children.filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== 'N/A')
  if (rows.length === 0) return null
  return (
    <div className={styles.detailSection}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {rows.map(([label, value]) => (
        <div className={styles.detailRow} key={label}>
          <span className={styles.detailLabel}>{label}</span>
          <span className={styles.detailValue}>{String(value)}</span>
        </div>
      ))}
    </div>
  )
}

const Candidates = () => {
  const navigate = useNavigate()
  const appStateContext = useContext(AppStateContext)
  const [candidates, setCandidates] = useState<CandidateSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [filterIds, setFilterIds] = useState<number[] | null>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(200)
  const allCandidatesRef = useRef<CandidateSummary[]>([])

  const [view, setView] = useState<'list' | 'detail'>('list')
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const loadCandidates = useCallback(async (p: number, ps: number) => {
    setLoading(true)
    const data = await getCandidateList(ps, p * ps)
    allCandidatesRef.current = data.items
    setCandidates(data.items)
    setTotal(data.total)
    setLoading(false)
  }, [])

  useEffect(() => {
    loadCandidates(page, pageSize)
  }, [page, pageSize])

  useEffect(() => {
    const handler = () => { loadCandidates(page, pageSize) }
    window.addEventListener('app-data-changed', handler)
    return () => window.removeEventListener('app-data-changed', handler)
  }, [loadCandidates, page, pageSize])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.type === 'candidates' && detail?.ids) {
        setFilterIds(detail.ids)
      }
    }
    window.addEventListener('app-grid-filter', handler)
    return () => window.removeEventListener('app-grid-filter', handler)
  }, [])

  const displayed = filterIds
    ? allCandidatesRef.current.filter(c => filterIds.includes(c.id))
    : candidates

  const totalPages = Math.ceil(total / pageSize)

  useEffect(() => {
    let context = `User is viewing the Candidates page.`
    if (view === 'detail' && selectedCandidate) {
      context += ` Viewing detail for candidate ${selectedCandidate.first_name} ${selectedCandidate.last_name} (ID: ${selectedCandidate.id}).`
    } else if (filterIds) {
      context += ` Showing ${displayed.length} filtered candidates (from chatbot search). The grid is filtered by the chatbot search results.`
    } else if (candidates.length > 0) {
      context += ` ${candidates.length} candidates listed (page ${page + 1} of ${totalPages}, ${total} total).`
    }
    appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: context })
    return () => { appStateContext?.dispatch({ type: 'SET_SCREEN_CONTEXT', payload: '' }) }
  }, [candidates.length, filterIds, displayed.length, page, totalPages, total, view, selectedCandidate])

  const clearFilter = () => {
    setFilterIds(null)
  }

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value))
    setPage(0)
    setFilterIds(null)
  }

  const handleCandidateClick = async (id: number) => {
    setDetailLoading(true)
    setView('detail')
    const detail = await getCandidateDetail(id)
    setSelectedCandidate(detail)
    setDetailLoading(false)
  }

  const handleBackToList = () => {
    setView('list')
    setSelectedCandidate(null)
  }

  const inf = (c: CandidateDetail) => c.information || {}

  const parseJobDate = (raw: string | undefined, asCurrent = false): Date | null => {
    if (!raw || raw === 'N/A') return asCurrent ? new Date() : null
    const s = raw.trim()
    if (/^present$/i.test(s)) return new Date()
    const ym = s.match(/^(\d{4})-(\d{2})/)
    if (ym) return new Date(Number(ym[1]), Number(ym[2]) - 1, 1)
    const y = s.match(/^(\d{4})$/)
    if (y) return new Date(Number(y[1]), 0, 1)
    const d = new Date(s)
    return isNaN(d.getTime()) ? null : d
  }

  const detectExpGaps = (entries: any[]): (number | null)[] => {
    const gaps: (number | null)[] = [null]
    for (let i = 1; i < entries.length; i++) {
      const prev = entries[i - 1]
      const curr = entries[i]
      const prevStart = parseJobDate(prev.start_date)
      const currEnd   = parseJobDate(curr.end_date) ?? parseJobDate(curr.start_date)
      if (!prevStart || !currEnd) { gaps.push(null); continue }
      const gap = (prevStart.getFullYear() - currEnd.getFullYear()) * 12 + (prevStart.getMonth() - currEnd.getMonth())
      gaps.push(gap >= 3 ? gap : null)
    }
    return gaps
  }

  const normaliseExp = (info: Record<string, any>): any[] => {
    const entries: any[] = []
    for (const key of ['experience', 'job_history', 'work_experience', 'work_history']) {
      if (Array.isArray(info[key])) entries.push(...info[key])
    }
    return entries.map(e => {
      if (!e || typeof e !== 'object') return null
      const title = e.title || e.job_title || e.position || e.role || ''
      const company = e.company || e.employer || e.organisation || e.organization || ''
      const location = e.location || ''
      const startDate = e.start_date || e.from || e.from_date || ''
      let endDate = e.end_date || e.to || e.to_date || ''
      if (e.is_current && !endDate) endDate = 'Present'
      let description = e.description || e.summary || ''
      const resp = e.responsibilities || e.duties || []
      if (Array.isArray(resp) && resp.length) {
        const joined = resp.map((r: any) => `• ${r}`).join('\n')
        description = description ? `${description}\n${joined}` : joined
      }
      return { title, company, location, start_date: startDate, end_date: endDate, description }
    }).filter(Boolean).filter((e: any) => e.title || e.company)
  }

  const toSkillList = (val: any): string[] => {
    if (Array.isArray(val)) return val.filter(v => typeof v === 'string')
    if (typeof val === 'string' && val) return val.split(',').map((s: string) => s.trim()).filter(Boolean)
    return []
  }

  const DETAIL_KNOWN = new Set([
    'first_name','last_name','full_name','email','phone','location',
    'address','city','state','country','postcode','date_of_birth','nationality','work_rights',
    'linkedin_url','github_url','website','portfolio_url',
    'headline','current_role','current_company','summary','bio',
    'skills','experience','job_history','work_experience','work_history',
    'education','certifications','languages',
    'available_for_work','available_from_date','availability_notes','notice_period_weeks',
    'notice_period','work_preferences','willing_to_relocate','desired_salary',
    'status','disabled_reason','disabled_date','cv_text','informationvector',
    'availability','salary_expectation','experience_years','job_title',
  ])

  if (view === 'detail') {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <DefaultButton
            className={styles.backButton}
            iconProps={{ iconName: 'Back' }}
            onClick={handleBackToList}
            title="Back to list"
          />
          <h1 className={styles.title}>Candidate Detail</h1>
        </div>
        {detailLoading ? (
          <div className={styles.loading}>
            <Spinner size={SpinnerSize.large} label="Loading candidate..." />
          </div>
        ) : !selectedCandidate ? (
          <div className={styles.emptyState}>Candidate not found.</div>
        ) : (
          <div className={styles.detailCard}>
            {/* ── Header ── */}
            <h2 className={styles.detailName}>
              {inf(selectedCandidate).full_name || `${selectedCandidate.first_name} ${selectedCandidate.last_name}`.trim() || `Candidate ${selectedCandidate.id}`}
              {selectedCandidate.status === 'disabled' && (
                <span className={styles.disabledBadge} style={{ marginLeft: 12, verticalAlign: 'middle' }}>Disabled</span>
              )}
            </h2>
            {selectedCandidate.job_title && <div style={{ color: '#555', marginBottom: 4 }}>{selectedCandidate.job_title}</div>}
            <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>
              ID: {selectedCandidate.id} · Created: {selectedCandidate.created} · Modified: {selectedCandidate.modified}
            </div>

            {selectedCandidate.status === 'disabled' && (
              <div className={styles.statusBanner}>
                <strong>Disabled</strong> — {selectedCandidate.disabled_reason || 'No reason provided'}
                {selectedCandidate.disabled_date && <span> (since {selectedCandidate.disabled_date})</span>}
              </div>
            )}

            <div className={styles.detailActions}>
              {selectedCandidate.status !== 'disabled' ? (
                <button className={styles.disableButton} onClick={async () => {
                  const reason = window.prompt('Reason for disabling this candidate:')
                  if (reason) {
                    const ok = await disableCandidate(selectedCandidate.id, reason)
                    if (ok) { const d = await getCandidateDetail(selectedCandidate.id); setSelectedCandidate(d); loadCandidates(page, pageSize) }
                  }
                }}>Disable Candidate</button>
              ) : (
                <button className={styles.enableButton} onClick={async () => {
                  const ok = await enableCandidate(selectedCandidate.id)
                  if (ok) { const d = await getCandidateDetail(selectedCandidate.id); setSelectedCandidate(d); loadCandidates(page, pageSize) }
                }}>Enable Candidate</button>
              )}
            </div>

            {/* ── Contact ── */}
            <DetailSection title="Contact details">
              {[
                ['Email', inf(selectedCandidate).email || selectedCandidate.email],
                ['Phone', inf(selectedCandidate).phone],
                ['Location', inf(selectedCandidate).location],
                ['Address', inf(selectedCandidate).address],
                ['City', inf(selectedCandidate).city],
                ['State', inf(selectedCandidate).state],
                ['Country', inf(selectedCandidate).country],
                ['Postcode', inf(selectedCandidate).postcode],
                ['Date of birth', inf(selectedCandidate).date_of_birth],
                ['Nationality', inf(selectedCandidate).nationality],
                ['Work rights', inf(selectedCandidate).work_rights],
                ['LinkedIn', inf(selectedCandidate).linkedin_url],
                ['GitHub', inf(selectedCandidate).github_url],
                ['Website', inf(selectedCandidate).website || inf(selectedCandidate).portfolio_url],
              ]}
            </DetailSection>

            {/* ── Professional summary ── */}
            <DetailSection title="Professional summary">
              {[
                ['Headline', inf(selectedCandidate).headline],
                ['Current role', inf(selectedCandidate).current_role],
                ['Current company', inf(selectedCandidate).current_company],
                ['Summary', inf(selectedCandidate).summary || inf(selectedCandidate).bio],
              ]}
            </DetailSection>

            {/* ── Skills ── */}
            {toSkillList(inf(selectedCandidate).skills).length > 0 && (
              <div className={styles.detailSection}>
                <h3 className={styles.sectionTitle}>Skills</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {toSkillList(inf(selectedCandidate).skills).map((s, i) => (
                    <span key={i} className={styles.tag}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* ── Work experience ── */}
            {(() => {
              const expEntries = normaliseExp(inf(selectedCandidate))
              if (expEntries.length === 0) return null
              const expGaps = detectExpGaps(expEntries)
              return (
                <div className={styles.detailSection}>
                  <h3 className={styles.sectionTitle}>Work experience</h3>
                  {expEntries.map((e: any, i: number) => (
                    <Fragment key={i}>
                      {expGaps[i] != null && (
                        <div style={{
                          margin: '0 0 14px',
                          padding: '8px 14px',
                          background: '#fff8e1',
                          border: '1px solid #ffc107',
                          borderRadius: 8,
                          fontSize: 13,
                          color: '#7a5000',
                          display: 'flex',
                          gap: 8,
                          alignItems: 'center',
                        }}>
                          <span>⚠️</span>
                          <span><strong>{expGaps[i]}-month gap</strong> — no employment recorded during this period.</span>
                        </div>
                      )}
                      <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: i < expEntries.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{e.title || '—'}</div>
                        {e.company && <div style={{ fontSize: 14, color: '#444', marginTop: 2 }}>{e.company}{e.location ? ` · ${e.location}` : ''}</div>}
                        {(e.start_date || e.end_date) && <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{e.start_date || '?'} – {e.end_date || 'Present'}</div>}
                        {e.description && <div style={{ fontSize: 13, marginTop: 6, whiteSpace: 'pre-line', color: '#333' }}>{e.description}</div>}
                      </div>
                    </Fragment>
                  ))}
                </div>
              )
            })()}

            {/* ── Education ── */}
            {Array.isArray(inf(selectedCandidate).education) && inf(selectedCandidate).education.length > 0 && (
              <div className={styles.detailSection}>
                <h3 className={styles.sectionTitle}>Education</h3>
                {inf(selectedCandidate).education.map((e: any, i: number) => (
                  <div key={i} style={{ marginBottom: 10, fontSize: 14 }}>
                    <strong>{e.qualification || e.degree || e.field_of_study || '—'}</strong>
                    {(e.institution || e.school) ? ` — ${e.institution || e.school}` : ''}
                    {(e.year_completed || e.end_date) ? <span style={{ color: '#888' }}> ({e.year_completed || e.end_date})</span> : null}
                  </div>
                ))}
              </div>
            )}

            {/* ── Certifications & languages ── */}
            {(Array.isArray(inf(selectedCandidate).certifications) && inf(selectedCandidate).certifications.length > 0) && (
              <div className={styles.detailSection}>
                <h3 className={styles.sectionTitle}>Certifications</h3>
                {inf(selectedCandidate).certifications.map((c: any, i: number) => (
                  <div key={i} style={{ fontSize: 14, marginBottom: 4 }}>
                    <strong>{typeof c === 'string' ? c : (c.name || c.title || '—')}</strong>
                    {typeof c === 'object' && c.issuer ? <span style={{ color: '#888' }}> — {c.issuer}</span> : null}
                    {typeof c === 'object' && (c.date || c.year) ? <span style={{ color: '#888' }}> ({c.date || c.year})</span> : null}
                  </div>
                ))}
              </div>
            )}
            {toSkillList(inf(selectedCandidate).languages).length > 0 && (
              <div className={styles.detailSection}>
                <h3 className={styles.sectionTitle}>Languages</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {toSkillList(inf(selectedCandidate).languages).map((l, i) => <span key={i} className={styles.tag}>{l}</span>)}
                </div>
              </div>
            )}

            {/* ── Availability & preferences ── */}
            <DetailSection title="Availability & preferences">
              {[
                ['Available for work', inf(selectedCandidate).available_for_work != null ? (inf(selectedCandidate).available_for_work ? 'Yes' : 'No') : undefined],
                ['Available from', inf(selectedCandidate).available_from_date],
                ['Notice period', inf(selectedCandidate).notice_period || (inf(selectedCandidate).notice_period_weeks != null ? `${inf(selectedCandidate).notice_period_weeks} week(s)` : undefined)],
                ['Availability notes', inf(selectedCandidate).availability_notes],
                ['Willing to relocate', inf(selectedCandidate).willing_to_relocate != null ? (inf(selectedCandidate).willing_to_relocate ? 'Yes' : 'No') : undefined],
              ]}
            </DetailSection>

            {/* ── Work preferences ── */}
            {inf(selectedCandidate).work_preferences && (
              <DetailSection title="Work preferences">
                {Object.entries(inf(selectedCandidate).work_preferences as Record<string, any>).map(([k, v]) => [
                  k.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
                  Array.isArray(v) ? v.join(', ') : String(v ?? ''),
                ] as [string, string])}
              </DetailSection>
            )}

            {/* ── Additional (catch-all) ── */}
            {Object.entries(inf(selectedCandidate)).filter(([k, v]) =>
              !DETAIL_KNOWN.has(k) && v !== null && v !== undefined && v !== ''
              && !(Array.isArray(v) && v.length === 0)
            ).length > 0 && (
              <div className={styles.detailSection}>
                <h3 className={styles.sectionTitle}>Additional information</h3>
                {Object.entries(inf(selectedCandidate))
                  .filter(([k, v]) => !DETAIL_KNOWN.has(k) && v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
                  .map(([k, v]) => (
                    <div key={k} className={styles.detailRow}>
                      <span className={styles.detailLabel}>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                      <span className={styles.detailValue}>{Array.isArray(v) ? (v as any[]).join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <DefaultButton
          className={styles.backButton}
          iconProps={{ iconName: 'Back' }}
          onClick={() => navigate('/')}
          title="Back to chat"
        />
        <h1 className={styles.title}>Candidates</h1>
        {filterIds && (
          <div className={styles.filterBar}>
            <span className={styles.filterLabel}>Filtered: {displayed.length} of {allCandidatesRef.current.length}</span>
            <DefaultButton
              className={styles.clearFilterButton}
              text="Show All"
              onClick={clearFilter}
            />
          </div>
        )}
      </div>
      {loading ? (
        <div className={styles.loading}>
          <Spinner size={SpinnerSize.large} label="Loading candidates..." />
        </div>
      ) : displayed.length === 0 ? (
        <div className={styles.emptyState}>
          {filterIds ? 'No candidates match the current filter.' : 'No candidates found.'}
          {filterIds && (
            <DefaultButton
              className={styles.clearFilterButton}
              text="Show All"
              onClick={clearFilter}
              style={{ marginTop: 12 }}
            />
          )}
        </div>
      ) : (
        <>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Job Title</th>
                <th>Location</th>
                <th>Availability</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(c => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>
                    <span className={styles.clickableLink} onClick={() => handleCandidateClick(c.id)}>
                      {c.name}
                    </span>
                    {c.status === 'disabled' && (
                      <span className={styles.disabledBadge}>Disabled</span>
                    )}
                  </td>
                  <td>{c.job_title !== 'N/A' ? c.job_title : '-'}</td>
                  <td>{c.location !== 'N/A' ? c.location : '-'}</td>
                  <td>
                    {c.availability && c.availability !== 'N/A' ? (
                      <span className={styles.availabilityBadge}>{c.availability}</span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filterIds && (
            <div className={styles.pagination}>
              <DefaultButton
                className={styles.pageButton}
                text="Previous"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              />
              <span className={styles.pageInfo}>
                Page {page + 1} of {totalPages} ({total} total)
              </span>
              <DefaultButton
                className={styles.pageButton}
                text="Next"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(p => p + 1)}
              />
              <select className={styles.pageSizeSelect} value={pageSize} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map(s => (
                  <option key={s} value={s}>{s} per page</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Candidates
